/**
 * Convert between Supabase rows (snake_case, DB-typed) and the frontend's
 * camelCase entities. Lets us keep components unchanged after moving from
 * localStorage to Supabase.
 */

import type { Database } from "@/types/database";
import { parseProgressSourceRefs } from "./progressSourceRefs";
import type {
  Workspace,
  User,
  Team,
  Label,
  Project,
  ProjectSource,
  ProjectMilestone,
  Cycle,
  Issue,
  Doc,
  Note,
  ProgressCard,
  Feedback,
  WorkspaceLink,
  AgentRun,
  AgentSession,
  Comment,
  FeedbackStatus,
  FeedbackSeverity,
  StatusId,
  PriorityId,
  ProjectStatus,
  ProgressCardStatus,
} from "./types";
import { parseLiveRunActions } from "./liveAgentRuns";
import { parseDateOnlyTimestamp } from "./dateOnly";
import { isTaskReportChannel } from "./taskReportOrigin";

type WorkspaceRow = Database["public"]["Tables"]["workspaces"]["Row"];
type TeamRow = Database["public"]["Tables"]["teams"]["Row"];
type LabelRow = Database["public"]["Tables"]["labels"]["Row"];
type ProjectRow = Database["public"]["Tables"]["projects"]["Row"];
type ProjectSourceRow = Database["public"]["Tables"]["project_sources"]["Row"];
type ProjectMilestoneRow = Database["public"]["Tables"]["project_milestones"]["Row"];
type CycleRow = Database["public"]["Tables"]["cycles"]["Row"];
// Mappers below Omit columns the frontend never reads — search_doc (heavy
// tsvector for FTS) and updated_by — so callers can narrow their select()
// strings accordingly without re-fetching unnecessary bytes.
type IssueRow = Omit<Database["public"]["Tables"]["issues"]["Row"], "search_doc" | "updated_by" | "made_public_at" | "made_public_by">;
type DocRow = Omit<Database["public"]["Tables"]["docs"]["Row"], "search_doc" | "updated_by" | "made_public_at" | "made_public_by" | "doc_number">;
type NoteRow = Omit<Database["public"]["Tables"]["notes"]["Row"], "search_doc">;
type CardRow = Omit<Database["public"]["Tables"]["progress_cards"]["Row"], "search_doc" | "updated_by">;
type ProfileRow = Database["public"]["Tables"]["profiles"]["Row"];
type CommentRow = Database["public"]["Tables"]["comments"]["Row"];
type FeedbackRow = Database["public"]["Tables"]["feedback"]["Row"];
type WorkspaceLinkRow = Database["public"]["Tables"]["workspace_links"]["Row"];
type AgentRunRow = Database["public"]["Tables"]["agent_runs"]["Row"];
type AgentSessionRow = Omit<Database["public"]["Tables"]["agent_sessions"]["Row"], "search_doc">;

type DbPriority = Database["public"]["Enums"]["issue_priority"];

const PRIORITY_MAP: Record<DbPriority, PriorityId> = {
  none: 0,
  urgent: 1,
  high: 2,
  medium: 3,
  low: 4,
};

export const PRIORITY_TO_DB: Record<PriorityId, DbPriority> = {
  0: "none",
  1: "urgent",
  2: "high",
  3: "medium",
  4: "low",
};

export function mapWorkspace(r: WorkspaceRow): Workspace {
  return {
    id: r.id,
    name: r.name,
    slug: r.slug,
    icon: r.icon,
    color: r.color,
    description: r.description,
    category: (r.category ?? "client") as Workspace["category"],
    createdAt: Date.parse(r.created_at),
  };
}

export function mapProfile(r: ProfileRow): User {
  return {
    id: r.id,
    name: r.name || r.email.split("@")[0],
    email: r.email,
    initials: r.initials || r.name.slice(0, 2).toUpperCase(),
    color: r.color,
    isBot: r.is_bot ?? false,
    botType: r.bot_type ?? null,
  };
}

export function mapTeam(
  r: TeamRow,
  memberIds: readonly string[] = []
): Team {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    key: r.key,
    name: r.name,
    icon: r.icon,
    color: r.color,
    description: r.description || "",
    memberIds: [...memberIds],
  };
}

export function mapLabel(r: LabelRow): Label {
  return { id: r.id, teamId: r.team_id, name: r.name, color: r.color };
}

export function mapProject(
  r: ProjectRow,
  teamIds: string[] = []
): Project {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    name: r.name,
    description: r.description,
    teamIds,
    leadId: r.lead_id,
    status: r.status as ProjectStatus,
    icon: r.icon,
    color: r.color,
    startDate: parseDateOnlyTimestamp(r.start_date),
    targetDate: parseDateOnlyTimestamp(r.target_date),
    createdAt: Date.parse(r.created_at),
    updatedAt: Date.parse(r.updated_at),
  };
}

export function mapProjectSource(r: ProjectSourceRow): ProjectSource {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    projectId: r.project_id,
    provider: r.provider as ProjectSource["provider"],
    repository: r.repository,
    url: r.url,
    defaultBranch: r.default_branch,
    pathRules: r.path_rules,
    labelRules: r.label_rules,
    isPrimary: r.is_primary,
    syncEnabled: r.sync_enabled,
    lastSyncedAt: r.last_synced_at ? Date.parse(r.last_synced_at) : null,
    createdAt: Date.parse(r.created_at),
    updatedAt: Date.parse(r.updated_at),
  };
}

export function mapProjectMilestone(r: ProjectMilestoneRow): ProjectMilestone {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    projectId: r.project_id,
    title: r.title,
    description: r.description,
    targetDate: Date.parse(`${r.target_date}T12:00:00`),
    status: r.status as ProjectMilestone["status"],
    position: r.position,
    linkedIssueIds: r.linked_issue_ids,
    createdBy: r.created_by,
    updatedBy: r.updated_by,
    createdAt: Date.parse(r.created_at),
    updatedAt: Date.parse(r.updated_at),
  };
}

export function mapCycle(r: CycleRow): Cycle {
  return {
    id: r.id,
    teamId: r.team_id,
    number: r.number,
    name: r.name,
    startDate: Date.parse(r.start_date),
    endDate: Date.parse(r.end_date),
  };
}

export function mapIssue(
  r: IssueRow,
  labelIds: string[] = [],
  comments: CommentRow[] = []
): Issue {
  const executorAgentId = r.executor_agent_id ?? null;
  return {
    id: r.id,
    identifier: r.identifier,
    workspaceId: r.workspace_id,
    teamId: r.team_id,
    title: r.title,
    description: r.description,
    status: r.status as StatusId,
    isBlocked: r.is_blocked === true,
    priority: PRIORITY_MAP[r.priority as DbPriority] ?? 0,
    assigneeId: r.assignee_id,
    ownerId:
      r.owner_id ?? (executorAgentId === null ? r.assignee_id : null),
    executorAgentId,
    labelIds,
    projectId: r.project_id,
    cycleId: r.cycle_id,
    parentId: r.parent_id,
    authorId: r.author_id ?? null,
    requesterId: r.requester_id ?? null,
    reportChannel: isTaskReportChannel(r.report_channel) ? r.report_channel : null,
    reportContext: r.report_context ?? null,
    reportUrl: r.report_url ?? null,
    createdAt: Date.parse(r.created_at),
    updatedAt: Date.parse(r.updated_at),
    completedAt: r.completed_at ? Date.parse(r.completed_at) : null,
    dueDate: r.due_date ? Date.parse(r.due_date) : null,
    estimate: r.estimate,
    isPublic: r.is_public,
    subscribers: [],
    comments: comments.map(mapComment),
  };
}

export function mapComment(c: CommentRow): Comment {
  return {
    id: c.id,
    parentType: (c.parent_type as "issue" | "doc" | "note") ?? "issue",
    parentId: (c.parent_id as string) ?? c.issue_id ?? "",
    issueId: c.issue_id,
    userId: c.author_id,
    body: c.body,
    replyToId: c.reply_to_id ?? null,
    createdAt: Date.parse(c.created_at),
  };
}

export function mapDoc(r: DocRow): Doc {
  return {
    id: r.id,
    identifier: r.identifier ?? null,
    workspaceId: r.workspace_id,
    projectId: r.project_id,
    teamId: r.team_id,
    parentId: (r as unknown as { parent_id: string | null }).parent_id ?? null,
    position: (r as unknown as { position: number }).position ?? 0,
    title: r.title,
    content: r.content,
    icon: r.icon,
    isPublic: r.is_public,
    authorId: r.author_id || "",
    tags: r.tags,
    pinned: r.pinned,
    createdAt: Date.parse(r.created_at),
    updatedAt: Date.parse(r.updated_at),
  };
}

export function mapNote(r: NoteRow): Note {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    projectId: r.project_id,
    teamId: r.team_id,
    authorId: r.author_id,
    body: r.body,
    tags: r.tags,
    pinned: r.pinned,
    createdAt: Date.parse(r.created_at),
    updatedAt: Date.parse(r.updated_at),
  };
}

export function mapFeedback(r: FeedbackRow): Feedback {
  return {
    id: r.id,
    sourceWorkspaceId: r.source_workspace_id,
    targetWorkspaceId: r.target_workspace_id,
    title: r.title,
    body: r.body,
    severity: r.severity as FeedbackSeverity,
    status: r.status as FeedbackStatus,
    tags: r.tags,
    sourceDocId: r.source_doc_id,
    submitterId: r.submitter_id,
    linkedIssueId: r.linked_issue_id,
    triagedBy: r.triaged_by,
    triagedAt: r.triaged_at ? Date.parse(r.triaged_at) : null,
    resolvedAt: r.resolved_at ? Date.parse(r.resolved_at) : null,
    createdAt: Date.parse(r.created_at),
    updatedAt: Date.parse(r.updated_at),
  };
}

export function mapWorkspaceLink(r: WorkspaceLinkRow): WorkspaceLink {
  return {
    sourceWorkspaceId: r.source_workspace_id,
    targetWorkspaceId: r.target_workspace_id,
    createdBy: r.created_by,
    createdAt: Date.parse(r.created_at),
  };
}

export function mapAgentSession(r: AgentSessionRow): AgentSession {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    authorId: r.author_id,
    name: r.name,
    summary: r.summary,
    nextSteps: r.next_steps,
    openQuestions: r.open_questions,
    touchedIssueIds: r.touched_issue_ids,
    context:
      r.context && typeof r.context === "object" && !Array.isArray(r.context)
        ? (r.context as Record<string, unknown>)
        : {},
    startedAt: Date.parse(r.started_at),
    endedAt: r.ended_at ? Date.parse(r.ended_at) : null,
    createdAt: Date.parse(r.created_at),
    updatedAt: Date.parse(r.updated_at),
  };
}

export function mapAgentRun(r: AgentRunRow): AgentRun {
  const { actions, planSteps } = parseLiveRunActions(r.actions);
  return {
    id: r.id,
    sessionId: r.session_id,
    workspaceId: r.workspace_id,
    actorId: r.actor_id,
    prompt: r.prompt,
    summary: r.summary,
    actions,
    touchedIssueIds: r.touched_issue_ids,
    touchedDocIds: r.touched_doc_ids,
    projectIds: r.project_ids,
    startedAt: Date.parse(r.started_at),
    endedAt: r.ended_at ? Date.parse(r.ended_at) : null,
    status: r.status as AgentRun["status"],
    durationMs: r.duration_ms,
    createdAt: Date.parse(r.created_at),
    updatedAt: Date.parse(r.updated_at),
    heartbeatAt: r.heartbeat_at ? Date.parse(r.heartbeat_at) : null,
    currentStep: r.current_step,
    externalRunKey: r.external_run_key,
    parentRunId: r.parent_run_id,
    agentLabel: r.agent_label,
    source: r.source,
    planSteps,
    replacesRunId: r.replaces_run_id,
  };
}

export function mapProgressCard(r: CardRow): ProgressCard {
  const metricsArr = Array.isArray(r.metrics) ? (r.metrics as unknown[]) : [];
  const sourceRefs = parseProgressSourceRefs(r.source_refs).refs;
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    projectId: r.project_id,
    teamId: r.team_id,
    title: r.title,
    status: r.status as ProgressCardStatus,
    periodStart: Date.parse(r.period_start),
    periodEnd: Date.parse(r.period_end),
    summary: r.summary,
    highlights: r.highlights,
    blockers: r.blockers,
    nextSteps: r.next_steps,
    metrics: metricsArr as unknown as ProgressCard["metrics"],
    issueIds: r.issue_ids,
    authorId: r.author_id || "",
    source: r.source as ProgressCard["source"],
    externalKey: r.external_key,
    sourceRefs,
    createdAt: Date.parse(r.created_at),
    updatedAt: Date.parse(r.updated_at),
  };
}
